import {
  codePatternProblem,
  hostProblem,
  MAX_CREDENTIAL_HOSTS,
  MAX_LABEL_LENGTH,
  MAX_SELECTOR_LENGTH,
  MAX_SENDER_DOMAINS,
  MAX_SYSTEM_LENGTH,
  MAX_USERNAME_LENGTH,
  normalHost,
  REPEATED_DOMAIN_MESSAGE,
  REPEATED_HOST_MESSAGE,
  secretProblem,
  senderDomainProblem,
} from "@iqa/contracts/credential-rules";
import type { Credential, SaveCredentialArgs } from "./api.ts";

/**
 * The saved-login form's model (docs/credentials.md#the-admin-portal): what a producer types, the
 * problems the data API would refuse it for (the contracts' rules, without zod), and the change it
 * makes. The password is write-only: a draft of a saved login starts without one, and an empty one
 * on a change means "unchanged".
 */

export type CodeKind = Credential["code"];

export const CODE_KINDS: readonly { readonly value: CodeKind; readonly label: string }[] = [
  { value: "none", label: "None" },
  { value: "email", label: "Emailed code" },
  { value: "text", label: "Texted code" },
];

/** How the list names a code kind. */
export function codeText(kind: CodeKind): string {
  return kind === "email" ? "emailed" : kind === "text" ? "texted" : "none";
}

export const FORM_FIELDS = [
  { key: "username", label: "User ID field" },
  { key: "password", label: "Password field" },
  { key: "submit", label: "Submit button" },
  { key: "code", label: "Code field" },
  { key: "remember", label: '"Remember this device" checkbox' },
  { key: "method", label: "Emailed-code choice" },
] as const;

export type FormKey = (typeof FORM_FIELDS)[number]["key"];

export interface LoginDraft {
  readonly label: string;
  readonly system: string;
  /** As typed, one per row; empty rows are left out. */
  readonly hosts: readonly string[];
  readonly username: string;
  /** Write-only: never a saved login's; empty on a change keeps the saved one. */
  readonly password: string;
  readonly code: CodeKind;
  /** Only for emailed codes. */
  readonly senderDomains: readonly string[];
  readonly codePattern: string;
  /** CSS selectors; empty is none. */
  readonly form: Readonly<Record<FormKey, string>>;
}

/** A login's selectors as the form edits them: each one, empty where it has none. */
function selectorsOf(form: Credential["form"]): Record<FormKey, string> {
  return {
    username: form?.username ?? "",
    password: form?.password ?? "",
    submit: form?.submit ?? "",
    code: form?.code ?? "",
    remember: form?.remember ?? "",
    method: form?.method ?? "",
  };
}

const NO_FORM = selectorsOf(undefined);

/** A new login's draft: one empty host row. */
export function emptyDraft(): LoginDraft {
  return {
    label: "",
    system: "",
    hosts: [""],
    username: "",
    password: "",
    code: "none",
    senderDomains: [],
    codePattern: "",
    form: NO_FORM,
  };
}

/** A saved login's draft, to change it: everything but its password, which no answer holds. */
export function draftOf(login: Credential): LoginDraft {
  return {
    label: login.label,
    system: login.system,
    hosts: login.hosts.length > 0 ? [...login.hosts] : [""],
    username: login.username,
    password: "",
    code: login.code,
    senderDomains: [...login.senderDomains],
    codePattern: login.codePattern ?? "",
    form: selectorsOf(login.form),
  };
}

/** Problems by field: `label`, `hosts`, `hosts.<row>`, `senderDomains.<row>`, `form.<key>`… */
export type DraftProblems = Readonly<Record<string, string>>;

const filled = (rows: readonly string[]) =>
  rows.map((row, index) => ({ row, index })).filter(({ row }) => row.trim() !== "");

/**
 * What the data API would refuse in a draft, by field; empty when nothing. On a change, an empty
 * password is fine (it keeps the saved one).
 */
export function draftProblems(draft: LoginDraft, creating: boolean): DraftProblems {
  const problems: Record<string, string> = {};
  const text = (name: string, value: string, max: number, what: string) => {
    if (value.trim() === "") problems[name] = `${what} is needed`;
    else if (value.trim().length > max) problems[name] = `${what} is at most ${max} characters`;
  };
  text("label", draft.label, MAX_LABEL_LENGTH, "A label");
  text("system", draft.system, MAX_SYSTEM_LENGTH, "The system");
  text("username", draft.username, MAX_USERNAME_LENGTH, "The user ID");

  const hosts = filled(draft.hosts);
  if (hosts.length === 0) problems.hosts = "At least one host is needed";
  if (hosts.length > MAX_CREDENTIAL_HOSTS) {
    problems.hosts = `At most ${MAX_CREDENTIAL_HOSTS} hosts`;
  }
  const seenHosts = new Set<string>();
  for (const { row, index } of hosts) {
    const problem =
      hostProblem(row) ?? (seenHosts.has(normalHost(row)) ? REPEATED_HOST_MESSAGE : undefined);
    if (problem) problems[`hosts.${index}`] = problem;
    seenHosts.add(normalHost(row));
  }

  if (creating || draft.password !== "") {
    const problem = secretProblem(draft.password);
    if (problem)
      problems.password = creating && draft.password === "" ? "A password is needed" : problem;
  }

  if (draft.code === "email") {
    const domains = filled(draft.senderDomains);
    if (domains.length > MAX_SENDER_DOMAINS) {
      problems.senderDomains = `At most ${MAX_SENDER_DOMAINS} sender domains`;
    }
    const seenDomains = new Set<string>();
    for (const { row, index } of domains) {
      const problem =
        senderDomainProblem(row) ??
        (seenDomains.has(normalHost(row)) ? REPEATED_DOMAIN_MESSAGE : undefined);
      if (problem) problems[`senderDomains.${index}`] = problem;
      seenDomains.add(normalHost(row));
    }
    const pattern = codePatternProblem(draft.codePattern);
    if (pattern) problems.codePattern = pattern;
  }

  for (const { key } of FORM_FIELDS) {
    if (draft.form[key].trim().length > MAX_SELECTOR_LENGTH) {
      problems[`form.${key}`] = `A selector is at most ${MAX_SELECTOR_LENGTH} characters`;
    }
  }
  return problems;
}

const hostsOf = (rows: readonly string[]) => filled(rows).map(({ row }) => normalHost(row));

function formOf(form: LoginDraft["form"]): Partial<Record<FormKey, string>> {
  return Object.fromEntries(
    FORM_FIELDS.map(({ key }) => [key, form[key].trim()] as const).filter(
      ([, value]) => value !== "",
    ),
  );
}

const same = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

/**
 * The `saveCredential` arguments of a draft. A new login is created under `newId`, so sending it
 * again returns it rather than making a second. A change sends only what differs from `saved` (so a
 * sender learned meanwhile is kept) and the password only when one was typed; undefined when
 * nothing would change. Sender domains and the code pattern are for emailed codes only.
 */
export function saveArgsOf(
  draft: LoginDraft,
  saved: Credential | undefined,
  newId: string,
): SaveCredentialArgs | undefined {
  const hosts = hostsOf(draft.hosts);
  const senderDomains = hostsOf(draft.senderDomains);
  const codePattern = draft.codePattern.trim();
  const form = formOf(draft.form);
  const emailed = draft.code === "email";
  if (!saved) {
    return {
      id: newId,
      create: true,
      label: draft.label.trim(),
      system: draft.system.trim(),
      hosts,
      username: draft.username,
      secret: draft.password,
      code: draft.code,
      ...(emailed && senderDomains.length > 0 && { senderDomains }),
      ...(emailed && codePattern !== "" && { codePattern }),
      ...(Object.keys(form).length > 0 && { form }),
    };
  }
  const savedForm = formOf(selectorsOf(saved.form));
  const formChanged = FORM_FIELDS.some(({ key }) => (form[key] ?? "") !== (savedForm[key] ?? ""));
  const change: SaveCredentialArgs = {
    ...(draft.label.trim() !== saved.label && { label: draft.label.trim() }),
    ...(draft.system.trim() !== saved.system && { system: draft.system.trim() }),
    ...(!same(hosts, saved.hosts) && { hosts }),
    ...(draft.username !== saved.username && { username: draft.username }),
    ...(draft.password !== "" && { secret: draft.password }),
    ...(draft.code !== saved.code && { code: draft.code }),
    ...(emailed && !same(senderDomains, saved.senderDomains) && { senderDomains }),
    ...(emailed && codePattern !== (saved.codePattern ?? "") && { codePattern }),
    // The selectors are replaced together; {} clears them.
    ...(formChanged && { form }),
  };
  return Object.keys(change).length === 0 ? undefined : { id: saved.id, ...change };
}

/** A new login's id, made once per form so a repeated create returns the login it made. */
export function newLoginId(): string {
  return globalThis.crypto.randomUUID();
}
